import { getReaderChapterVoice, type ReaderBook, type ReaderChapter } from '@/services/booksApi';

/**
 * Every remote asset a chapter needs to render and play offline, as the
 * absolute CDN URLs the reader will ask for — the same strings that
 * `resolveAssetUrl` is keyed by.
 *
 * Shared between whatever decides to fetch (a buffer scheduler, a pin-book
 * action, a dev trigger) so that "what makes up a chapter" is defined once.
 *
 * In-chapter images are not listed here. Nothing in the payload enumerates
 * them; they are found by parsing the chapter HTML after it has been fetched,
 * which is the scheduler's job.
 */

export type ChapterAssetKind = 'html' | 'css' | 'tokenMap' | 'audio' | 'timings' | 'image';

export interface ChapterAssetRef {
  kind: ChapterAssetKind;
  cdnUrl: string;
  /** Set for the per-voice files (audio, timings); null for everything else. */
  voiceKey: string | null;
  /** The declared size, or null when the bundle did not say. Never zero-for-absent. */
  expectedBytes: number | null;
}

const declared = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * Ordered so that a partial fetch still leaves something useful: the text
 * first (the reader can render without narration), then the sync files,
 * then the audio, which is by far the largest.
 */
export const listChapterAssets = (
  book: { ebook: Pick<ReaderBook['ebook'], 'cssUrls'> },
  chapter: ReaderChapter,
  voiceKey: string | null | undefined,
): ChapterAssetRef[] => {
  const refs: ChapterAssetRef[] = [];
  const seen = new Set<string>();
  const add = (kind: ChapterAssetKind, cdnUrl: string | null, voice: string | null, bytes: number | null | undefined) => {
    if (!cdnUrl || seen.has(cdnUrl)) {
      return;
    }
    seen.add(cdnUrl);
    refs.push({ kind, cdnUrl, voiceKey: voice, expectedBytes: declared(bytes) });
  };

  add('html', chapter.htmlUrl, null, chapter.htmlBytes);
  for (const cssUrl of book.ebook.cssUrls) {
    add('css', cssUrl, null, null);
  }

  const voice = getReaderChapterVoice(chapter, voiceKey);
  if (voice) {
    add('tokenMap', chapter.tokenMapUrl, null, chapter.tokenMapBytes);
    add('timings', voice.timingsUrl, voiceKey ?? null, voice.timingsBytes);
    add('audio', voice.audioUrl, voiceKey ?? null, voice.audioBytes);
  }

  return refs;
};
